import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader, AlertCircle } from 'lucide-react';
import { useNotification } from '../context/NotificationContext';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }
        
        if (!session) {
          throw new Error('No session found');
        }

        console.log("Auth callback - session found:", session.user.email);

        // Check user permissions
        const { data: permissions, error: permError } = await supabase
          .from('user_permissions')
          .select('is_admin')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (permError) {
          console.error('Error checking permissions:', permError);
          // Continue anyway, might be a new user
        }

        // Redirect based on user role
        if (permissions?.is_admin) {
          showNotification('success', 'Successfully signed in as admin');
          navigate('/admin');
        } else {
          // Regular user
          showNotification('success', 'Successfully signed in');
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        setError(errorMessage);
        showNotification('error', errorMessage);
        
        // Redirect to home page after a delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate, showNotification]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-500 mb-6">
            <AlertCircle className="w-16 h-16 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Authentication Error
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {error}
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            Redirecting to home page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Loader className="w-12 h-12 animate-spin text-blue-600 mb-4" />
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
        Authenticating...
      </h1>
      <p className="text-gray-600 dark:text-gray-300">
        Please wait while we complete the sign-in process.
      </p>
    </div>
  );
}